import type { ExportData, ExportImage, ExportStory } from './exportData.ts';
import { mapsJson } from './atlas/mapExport.ts';
import { writeJson, type JsonValue as Json } from './jsonWriter.ts';

/**
 * The JSON export, as the desktop's `export.JsonExporter` writes it, byte for byte for
 * what the web covers: the same keys in the same order, and `export.JsonWriter`'s
 * formatting (two-space indents, `{}` and `[]` when empty, and only `"`, `\`, line
 * breaks, tabs and control characters escaped), maps included.
 */


export function exportJson(data: ExportData): string {
  const root = new Map<string, Json>([
    ['realm', new Map<string, Json>([
      ['name', data.realm.name],
      ['description', data.realm.description],
      ['audience', data.realm.audience],
    ])],
    ['entries', data.entries.map((entry) => new Map<string, Json>([
      ['id', entry.id],
      ['type', entry.typeName],
      ['title', entry.title],
      ['contentHtml', entry.contentHtml],
      ['locked', entry.locked],
      ['metadata', new Map<string, Json>(entry.metadata)],
      ['labels', [...entry.labels]],
      ['audience', entry.audience],
      ['links', entry.links.map((link) => new Map<string, Json>([
        ['targetEntryId', link.targetEntryId],
        ['targetTitle', link.targetTitle],
        ['relationshipType', link.relationshipType],
      ]))],
      ['images', entry.locked ? [] : entry.images.map(imageJson)],
      ['created', entry.created],
      ['modified', entry.modified],
    ]))],
    ['collections', data.collections.map((collection) => new Map<string, Json>([
      ['name', collection.name],
      ['audience', collection.audience],
      ['entries', collection.members.map((member) => member.title)],
    ]))],
    ['maps', mapsJson(data.atlas, new Map(data.entries.map((e) => [e.id, e.title])), base64)],
    ['stories', data.stories.map(storyJson)],
  ]);
  return writeJson(root);
}

/** `storiesJson`: acts, chapters and scenes in reading order; a new act or chapter whenever its title changes. */
function storyJson(story: ExportStory): Json {
  const acts: Map<string, Json>[] = [];
  let chapters: Map<string, Json>[] = [];
  let scenes: Json[] = [];
  let actTitle: string | null = null;
  let chapterTitle: string | null = null;
  for (const scene of story.scenes) {
    if (actTitle === null || scene.actTitle !== actTitle) {
      actTitle = scene.actTitle;
      chapters = [];
      acts.push(new Map<string, Json>([['title', actTitle], ['chapters', chapters]]));
      chapterTitle = null;
    }
    if (scene.chapterTitle !== chapterTitle) {
      chapterTitle = scene.chapterTitle;
      scenes = [];
      chapters.push(new Map<string, Json>([['title', chapterTitle], ['scenes', scenes]]));
    }
    scenes.push(new Map<string, Json>([
      ['id', scene.id],
      ['title', scene.title],
      ['contentHtml', scene.contentHtml],
      ['storyBeats', scene.storyBeats],
      ['words', scene.words],
      ['wordGoal', scene.wordGoal],
      ['referenceIds', [...scene.referenceIds]],
      ['linkedInTextIds', [...scene.linkedInTextIds]],
    ]));
  }
  return new Map<string, Json>([
    ['id', story.id],
    ['name', story.name],
    ['description', story.description],
    ['wordGoal', story.wordGoal],
    ['words', story.words],
    ['acts', acts],
  ]);
}

function imageJson(image: ExportImage): Json {
  return new Map<string, Json>([
    ['fileName', image.fileName],
    ['mimeType', image.mimeType],
    ['dataBase64', base64(image.data)],
  ]);
}

export function base64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}
